import React, { useState, useEffect } from "react";
import { Popover } from "@mui/material";
import { 
  CalendarMonth as CalendarIcon, 
  Clear as ClearIcon 
} from "@mui/icons-material";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Button,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  MenuItem,
  CircularProgress,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { apiServices } from "../../../services/apiServices";
import UserDetailsModal from "./UserDetailsModal";
import Swal from "sweetalert2";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [referralFilter, setReferralFilter] = useState(""); // Filter for referral type
const [dateRange, setDateRange] = useState({ from: "", to: "" });
const [dateAnchorEl, setDateAnchorEl] = useState(null);

const handleDateClick = (event) => setDateAnchorEl(event.currentTarget);
const handleDateClose = () => setDateAnchorEl(null);

const handleClearDates = (e) => {
  e.stopPropagation(); // Prevents the popup from opening when clicking 'X'
  setDateRange({ from: "", to: "" });
  setPage(0);
};

const getDisplayDate = () => {
  if (!dateRange.from && !dateRange.to) return "";
  return `${dateRange.from} to ${dateRange.to || "..."}`;
};

  // Countries for filter dropdown
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Modal state
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Email Preview states
  const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [selectedUserForEmail, setSelectedUserForEmail] = useState(null);

  // Fetch users from API
  const fetchUsers = async (pageNum = 0, limit = 25) => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: pageNum + 1, // API uses 1-based pagination
        limit: limit,
      });

      // Add search term if provided
      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      // Add filters if provided
      if (countryFilter) {
        params.append("country", countryFilter);
      }
      if (statusFilter) {
        params.append("activestatus", statusFilter);
      }
      if (paymentFilter) {
        params.append("payment_status", paymentFilter);
      }
      // Note: referral_type filter is handled client-side after fetching data

      // Add date filters if provided
 // NEW CODE
if (dateRange.from) {
  params.append("date_from", dateRange.from);
}
if (dateRange.to) {
  params.append("date_to", dateRange.to);
}


      const apiUrl = `users/listing?${params.toString()}`;
      console.log("API URL:", apiUrl);
      const response = await apiServices.getAPI(apiUrl);

      if (response.data.success) {
        let fetchedUsers = response.data.data.users;
        
        // Apply client-side referral filter
        if (referralFilter) {
          if (referralFilter === 'non_referral') {
            // Filter users with reference_by = DR-000000-IAPM
            fetchedUsers = fetchedUsers.filter(user => 
              user.reference_by === 'DR-000000-IAPM' || !user.reference_by
            );
          } else if (referralFilter === 'referral') {
            // Filter users with reference_by != DR-000000-IAPM
            fetchedUsers = fetchedUsers.filter(user => 
              user.reference_by && user.reference_by !== 'DR-000000-IAPM'
            );
          }
        }
        
        setUsers(fetchedUsers);
        // Update total count based on filtered results
        setTotalCount(referralFilter ? fetchedUsers.length : response.data.data.pagination.total_count);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Error fetching users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch countries for filter dropdown
  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await apiServices.getAPI("countries");
      if (response.data.countries && Array.isArray(response.data.countries)) {
        setCountries(response.data.countries);
      } else {
        console.error("Invalid countries data format:", response.data);
        setCountries([]);
      }
    } catch (err) {
      console.error("Error fetching countries:", err);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle filter changes
  const handleFilterChange = () => {
    setPage(0);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setPage(0);
    fetchUsers(0, rowsPerPage);
  };

  // Handle export users
  const handleExportUsers = async () => {
    setLoading(true);
    setError("");

    try {
      console.log("Starting export of all users...");

      // First, get the total count to know how many pages we need
      const initialParams = new URLSearchParams({
        page: 1,
        limit: 100, // API maximum limit
      });

          // Add current filters if any
          if (searchTerm.trim()) {
            initialParams.append("search", searchTerm.trim());
          }
          if (countryFilter) {
            initialParams.append("country", countryFilter);
          }
          if (statusFilter) {
            initialParams.append("activestatus", statusFilter);
          }
          if (paymentFilter) {
            initialParams.append("payment_status", paymentFilter);
          }
          // Note: referral_type filter is handled client-side
          if (dateRange.from) {
            initialParams.append("date_from", dateRange.from);
          }
          if (dateRange.to) {
            initialParams.append("date_to", dateRange.to);
          }
      const initialResponse = await apiServices.getAPI(
        `users/listing?${initialParams.toString()}`
      );

      if (!initialResponse.data.success) {
        setError("Failed to fetch users for export");
        return;
      }

      const totalUsers = initialResponse.data.data.pagination.total_count;
      const totalPages = Math.ceil(totalUsers / 100);

      console.log(
        `Total users to export: ${totalUsers}, Total pages: ${totalPages}`
      );

      let allUsers = [...initialResponse.data.data.users];
      
      // Apply referral filter to initial batch
      if (referralFilter) {
        if (referralFilter === 'non_referral') {
          allUsers = allUsers.filter(user => 
            user.reference_by === 'DR-000000-IAPM' || !user.reference_by
          );
        } else if (referralFilter === 'referral') {
          allUsers = allUsers.filter(user => 
            user.reference_by && user.reference_by !== 'DR-000000-IAPM'
          );
        }
      }

      // Fetch remaining pages if there are more than 1 page
      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const pageParams = new URLSearchParams({
            page: page,
            limit: 100,
          });

          // Add current filters if any
          if (searchTerm.trim()) {
            pageParams.append("search", searchTerm.trim());
          }
          if (countryFilter) {
            pageParams.append("country", countryFilter);
          }
          if (statusFilter) {
            pageParams.append("activestatus", statusFilter);
          }
          if (paymentFilter) {
            pageParams.append("payment_status", paymentFilter);
          }
          // Note: referral_type filter is handled client-side
          if (dateRange.from) {
            pageParams.append("date_from", dateRange.from);
          }
          if (dateRange.to) {
            pageParams.append("date_to", dateRange.to);
          }

          const pageResponse = await apiServices.getAPI(
            `users/listing?${pageParams.toString()}`
          );

          if (pageResponse.data.success && pageResponse.data.data.users) {
            let pageUsers = pageResponse.data.data.users;
            
            // Apply referral filter to this batch
            if (referralFilter) {
              if (referralFilter === 'non_referral') {
                pageUsers = pageUsers.filter(user => 
                  user.reference_by === 'DR-000000-IAPM' || !user.reference_by
                );
              } else if (referralFilter === 'referral') {
                pageUsers = pageUsers.filter(user => 
                  user.reference_by && user.reference_by !== 'DR-000000-IAPM'
                );
              }
            }
            
            allUsers = [...allUsers, ...pageUsers];
            console.log(
              `Fetched page ${page}/${totalPages}, Total users so far: ${allUsers.length}`
            );
          } else {
            console.warn(`Failed to fetch page ${page}`);
          }
        }
      }

      console.log(`Exporting ${allUsers.length} users...`);

      // Download CSV file
      downloadCSV(allUsers, "client_management_export.csv");

      console.log("Export completed successfully");
    } catch (err) {
      console.error("Error exporting users:", err);
      setError("Error exporting users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Convert users data to tabular format (for CSV/Excel)
  const convertUsersToTabularFormat = (users) => {
    if (!users || users.length === 0) {
      return [];
    }

    // Define headers to match table columns exactly
    const headers = [
      "IAPM ID",
      "Name",
      "Email",
      "Phone",
      "Country",
      "Referral ID",
      "Registration Date",
      "Status",
      "Payment",
    ];

    // Convert users to data rows to match table structure
    const dataRows = users.map((user) => [
      user.peacekeeper_id || "",
      user.username || "",
      user.email || "",
      user.mobile || "",
      user.country || "",
      user.reference_by || "",
      formatDate(user.created_at),
      user.activestatus === "Y" ? "Active" : "Inactive",
      getPaymentStatusLabel(user.payment_status),
    ]);

    // Combine headers and rows
    return [headers, ...dataRows];
  };

  // Download CSV file
  const downloadCSV = (users, filename) => {
    try {
      // Convert users data to tabular format
      const csvData = convertUsersToTabularFormat(users);

      // Convert 2D array to CSV string
      const csvContent = csvData
        .map((row) =>
          row
            .map((cell) => {
              // Escape double quotes by doubling them
              const cellStr = String(cell).replace(/"/g, '""');
              // Wrap cell in quotes if it contains comma, newline, or double quote
              if (
                cellStr.includes(",") ||
                cellStr.includes("\n") ||
                cellStr.includes('"')
              ) {
                return `"${cellStr}"`;
              }
              return cellStr;
            })
            .join(",")
        )
        .join("\n");

      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create a temporary link element and trigger download
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      console.log(`CSV file ${filename} downloaded successfully`);
    } catch (error) {
      console.error("Error creating CSV file:", error);
      setError("Error creating CSV file. Please try again.");
    }
  };

  // Handle open user details modal
  const handleOpenModal = (userId) => {
    setSelectedUserId(userId);
    setModalOpen(true);
  };

  // Handle close user details modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUserId(null);
  };

  // Handle user updated
  const handleUserUpdated = () => {
    fetchUsers();
  };

  // Handle status toggle
  const handleStatusToggle = async (user) => {
    const newStatus = user.activestatus === "Y" ? "N" : "Y";
    const statusAction = newStatus === "Y" ? "activated" : "deactivated";
    setUpdatingStatus(user.userid);

    try {
      const response = await apiServices.patchAPI(
        `users/${user.userid}/status`,
        {
          activestatus: newStatus,
        }
      );

      if (response.data.success) {
        // Update the user in the local state
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.userid === user.userid ? { ...u, activestatus: newStatus } : u
          )
        );
        setError("");

        // Show success message (optional)
        console.log(
          `User ${statusAction} successfully:`,
          response.data.message
        );
      } else {
        setError(response.data.error || "Failed to update user status");
      }
    } catch (err) {
      console.error("Error updating user status:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Error updating user status. Please try again.";
      setError(errorMessage);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Handle refresh
 const handleRefresh = () => {
  setSearchTerm("");
  setCountryFilter("");
  setStatusFilter("");
  setPaymentFilter("");
  setReferralFilter("");
 setDateRange({ from: "", to: "" }); 
  setPage(0);
};


  // Handle viewing email preview
  const handleViewEmailPreview = async (user) => {
    setSelectedUserForEmail(user);
    setEmailPreviewModalOpen(true);
    setEmailPreviewData(null);
    setEmailPreviewLoading(true);

    try {
      const payload = {
        peacekeeper_id: user.peacekeeper_id || user.temp_peacekeeper_id
      };

      const response = await apiServices.postAPI('registration-email-preview', payload);

      if (response.data && response.data.success) {
        const apiData = response?.data;
        
        // Parse the API response and structure it for the preview
        const previewData = {
          peacekeeperId: apiData.peacekeeperId || apiData.peacekeeper_id || user.peacekeeper_id || user.temp_peacekeeper_id,
          email: apiData.email || apiData.recipientEmail || user.email,
          subject: apiData.subject || 'I Am Peacekeeper Movement - Registration Confirmation',
          registrationLink: apiData.registrationLink || apiData.registration_link || '',
          html: apiData.html || apiData.emailHtml || apiData.emailMessage || '',
          qrCodeDataUrl: apiData.qrCodeDataUrl || apiData.qr_code_data_url || apiData.qrCode || null,
          certificateIncluded: apiData.certificateIncluded || apiData.certificate_included || false,
          emailMessage: apiData.emailMessage || ''
        };
        
        setEmailPreviewData(previewData);
      } else {
        throw new Error('Failed to fetch email preview');
      }
    } catch (err) {
      console.error('Error fetching email preview:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load email preview. Please try again.',
      });
      handleCloseEmailPreview();
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  // Handle closing email preview modal
  const handleCloseEmailPreview = () => {
    setEmailPreviewModalOpen(false);
    setEmailPreviewData(null);
    setSelectedUserForEmail(null);
  };

  // Handle resending registration email
  const handleResendEmail = async () => {
    if (!selectedUserForEmail) return;

    setResendingEmail(true);

    try {
      const payload = {
        peacekeeper_id: selectedUserForEmail.peacekeeper_id || selectedUserForEmail.temp_peacekeeper_id
      };

      const response = await apiServices.postAPI('resend-registration-email', payload);

      if (response.data && response.data.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Email Sent!',
          text: `Registration email has been successfully resent to ${emailPreviewData?.email || selectedUserForEmail.email || 'the user'}.`,
          confirmButtonColor: '#214F7D'
        });
        // Close the email preview modal after successful send
        handleCloseEmailPreview();
      } else {
        throw new Error(response.data?.message || 'Failed to resend email');
      }
    } catch (err) {
      console.error('Error resending email:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to resend registration email. Please try again.',
      });
    } finally {
      setResendingEmail(false);
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "Y":
        return "success";
      case "N":
        return "error";
      default:
        return "default";
    }
  };

  // Get payment status display label
  const getPaymentStatusLabel = (status) => {
    if (status?.toUpperCase() === "COMPLETED") {
      return "Paid";
    }
    return status || "N/A";
  };

  // Get payment status chip color
  const getPaymentStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "success"; // Changed from 'warning' to 'success' since PENDING now displays as "Paid"
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  // Format date
  // const formatDate = (dateString) => {
  //   if (!dateString) return "N/A";
  //   return new Date(dateString).toLocaleDateString();
  // };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch users when dependencies change
 useEffect(() => {
  fetchUsers(page, rowsPerPage);
}, [page, rowsPerPage, dateRange]);


  // Fetch countries on component mount
  useEffect(() => {
    fetchCountries();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== "") {
        fetchUsers(0, rowsPerPage);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, rowsPerPage]);

  return (
    <Box sx={{ width: "100%", overflow: "hidden", pr: 4, mr: 4 }}>
      {/* Search and Filters */}
      <Card
        sx={{
          mb: 2,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
          mr: 2,
        }}
      >
        <CardContent sx={{ py: 2, px: 0, position: 'relative' }}>
          {/* All Filters in One Row */}
          <Grid
            container
            spacing={0.1}
            alignItems="center"
            justifyContent="center"
            wrap="nowrap"
            sx={{ overflowX: 'hidden', pl: 0 }}
          >
<Grid item sx={{ minWidth:100, flexShrink: 0 }}>
              <TextField
                fullWidth
                placeholder="Search by username, IAPM ID, or email..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment>
                      <SearchIcon sx={{ color: "#6c757d" }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#214F7D",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#214F7D",
                    },
                  },
                }}
              />
            </Grid>

<Grid item sx={{ minWidth: 100 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={countryFilter}
                  onChange={(e) => {
                    setCountryFilter(e.target.value);
                    handleFilterChange();
                  }}
                  label="Country"
                  disabled={loadingCountries}
                  sx={{
                    backgroundColor: "#fff",
                    "& .MuiSelect-select": { paddingRight: "32px" },
                  }}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {loadingCountries ? (
                    <MenuItem disabled>Loading countries...</MenuItem>
                  ) : (
                    countries.map((country) => (
                      <MenuItem
                        key={country.id || country.countryname}
                        value={country.countryname}
                      >
                        {country.countryname}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

<Grid item sx={{ minWidth: 90 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    handleFilterChange();
                  }}
                  label="Status"
                  sx={{
                    backgroundColor: "#fff",
                    "& .MuiSelect-select": { paddingRight: "32px" },
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Y">Active</MenuItem>
                  <MenuItem value="N">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

<Grid item sx={{ minWidth: 110}}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment</InputLabel>
                <Select
                  value={paymentFilter}
                  onChange={(e) => {
                    setPaymentFilter(e.target.value);
                    handleFilterChange();
                  }}
                  label="Payment"
                  sx={{
                    backgroundColor: "#fff",
                    "& .MuiSelect-select": { paddingRight: "32px" },
                  }}
                >
                  <MenuItem value="">All Payments</MenuItem>
                  <MenuItem value="COMPLETED">Paid</MenuItem>
                  <MenuItem value="COMPLIMENTARY">Complimentary</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Referral filter commented out */}

            <Grid item sx={{ minWidth:75,width:"20%" }}>
              <TextField
                fullWidth
                size="small"
                label="Registration Date"
                placeholder="Select date range"
                value={getDisplayDate()}
                onClick={handleDateClick}
                readOnly
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: dateRange.from && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearDates}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ cursor: 'pointer', "& .MuiOutlinedInput-input": { cursor: 'pointer' }, bgcolor: '#fff' }}
              />
            </Grid>

             <Box
  sx={{
    display: "flex",
    gap: 0.5,
    alignItems: "center",
    flexWrap: "nowrap",
  }}
>
  <Button
    variant="contained"
    onClick={handleApplyFilters}
    size="small"
    sx={{
      backgroundColor: "#214F7D",
      "&:hover": { backgroundColor: "#1a3f63" },
      textTransform: "none",
      fontWeight: 500,
      borderRadius: "6px",
    }}
  >
    Apply
  </Button>

  
  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
    <Tooltip title="Refresh">
      <IconButton
        onClick={handleRefresh}
        size="small"
        sx={{
          border: '1px solid #dee2e6',
          backgroundColor: '#fff',
          width: 36,
          height: 36,
          mb: 0.3,
          zIndex: 3,
          '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#214F7D'
          },
        }}
      >
        <RefreshIcon sx={{ fontSize: '1rem', color: '#28a745' }} />
      </IconButton>
    </Tooltip>

    <Button
      variant="outlined"
      onClick={handleExportUsers}
      size="small"
      disabled={users.length === 0}
      sx={{
        borderColor: "#28a745",
        color: "#28a745",
        "&:hover": {
          borderColor: "#1e7e34",
          backgroundColor: "rgba(40, 167, 69, 0.04)",
        },
        borderRadius: "6px",
        minWidth: 36,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <DownloadIcon />
    </Button>
  </Box>
</Box>
            </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <Paper
        sx={{
          width: "100%",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          overflow: "auto",
          pr: 2,
          mr: 2,
        }}
      >
        <TableContainer
          sx={{ maxHeight: 500, overflowX: "auto", overflowY: "auto" }}
        >
          <Table stickyHeader  sx={{
              minWidth: 1000,
              '& td, & th': { whiteSpace: 'nowrap' }
            }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 120,
                  }}
                >
                  IAPM ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 120,
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 150,
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 120,
                  }}
                >
                  Phone
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 120,
                  }}
                >
                  Gender
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 100,
                  }}
                >
                  Country
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  Referral ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 300,
                    color: "#214F7D",
                    fontSize: "0.875rem",
                    width:"50%",
                  }}
                >
                  Registration Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 100,
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 100,
                  }}
                >
                  Payment
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#214F7D",
                    py: 1,
                    fontSize: "0.875rem",
                    minWidth: 120,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 3,
                      }}
                    >
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      Loading users...
                    </Box>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 3 }}
                    >
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.userid}
                    hover
                    sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {user.peacekeeper_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.username}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.75rem", color: "#6c757d" }}
                      >
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.mobile}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.gender}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {user.country}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {user.reference_by}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {formatDate(user.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        label={
                          user.activestatus === "Y" ? "Active" : "Inactive"
                        }
                        color={getStatusColor(user.activestatus)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          "& .MuiChip-label": { fontSize: "0.75rem" },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        label={getPaymentStatusLabel(user.payment_status)}
                        color={getPaymentStatusColor(user.payment_status)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          "& .MuiChip-label": { fontSize: "0.75rem" },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ pr: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Tooltip title="View User Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenModal(user.userid)}
                            sx={{
                              width: 32,
                              height: 32,
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                              },
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            user.activestatus === "Y"
                              ? "Deactivate User"
                              : "Activate User"
                          }
                        >
                          <Switch
                            checked={user.activestatus === "Y"}
                            onChange={() => handleStatusToggle(user)}
                            disabled={updatingStatus === user.userid}
                            size="small"
                            color="success"
                          />
                        </Tooltip>
                        <Tooltip title="Email Preview">
                          <IconButton
                            size="small"
                            onClick={() => handleViewEmailPreview(user)}
                            sx={{
                              width: 32,
                              height: 32,
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                              },
                            }}
                          >
                            <EmailIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Summary */}
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          backgroundColor: "#f8f9fa",
          borderRadius: 1,
          border: "1px solid #e9ecef",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.875rem" }}
        >
          <strong>Total Users:</strong> {totalCount} | <strong>Showing:</strong>{" "}
          {users.length} of {totalCount}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.875rem" }}
        >
          Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
        </Typography>
      </Box>

      {/* User Details Modal */}
      <UserDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        userId={selectedUserId}
        onUserUpdated={handleUserUpdated}
      />

      {/* Email Preview Modal */}
      <Dialog
        open={emailPreviewModalOpen}
        onClose={handleCloseEmailPreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Email Preview - {selectedUserForEmail?.username || selectedUserForEmail?.email}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ 
          pb: 3, 
          pt: '24px !important', 
          px: 3, 
          maxHeight: '70vh',
          overflowY: 'auto'
        }}>
          <style>
            {`
              .emailPreview {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
              }
              .emailPreview .header {
                background: #214F7D;
                color: white;
                padding: 30px 20px;
                text-align: center;
              }
              .emailPreview .header h1 {
                margin: 0;
                font-size: 24px;
              }
              .emailPreview .content {
                padding: 30px 20px;
              }
              .emailPreview .content h2 {
                color: #214F7D;
                margin-top: 0;
              }
              .emailPreview .button-container {
                text-align: center;
                margin: 30px 0;
              }
              .emailPreview .button {
                display: inline-block;
                background: #28a745;
                color: white;
                padding: 15px 30px;
                text-decoration: none;
                border-radius: 5px;
                font-weight: bold;
                font-size: 16px;
              }
              .emailPreview .qr-section {
                background: #f8f9fa;
                padding: 25px;
                border-radius: 10px;
                margin: 25px 0;
                border-left: 4px solid #28a745;
                text-align: center;
              }
              .emailPreview .qr-section h3 {
                color: #28a745;
                margin: 0 0 15px 0;
                font-size: 20px;
              }
              .emailPreview .qr-code {
                text-align: center;
                margin: 15px auto;
              }
              .emailPreview .qr-code-table {
                width: 200px;
                height: 200px;
                border-spacing: 0;
                border-collapse: collapse;
                margin: 0 auto;
              }
              .emailPreview .qr-code-img {
                width: 200px;
                height: 200px;
                border: 3px solid white;
                display: block;
              }
              .emailPreview .qr-logo-cell {
                text-align: center;
                vertical-align: middle;
              }
              .emailPreview .qr-center-logo {
                display: inline-block;
                width: 45px;
                height: 45px;
                background: white;
                border-radius: 50%;
                text-align: center;
                line-height: 45px;
              }
              .emailPreview .qr-logo {
                width: 30px;
                height: 30px;
                vertical-align: middle;
              }
              .emailPreview .qr-note {
                margin-top: 15px;
                color: #214F7D;
                font-size: 14px;
                line-height: 1.5;
              }
              .emailPreview .certificate-section {
                background: #f8f9fa;
                padding: 25px;
                border-radius: 10px;
                margin: 25px 0;
                border-left: 4px solid #28a745;
              }
              .emailPreview .certificate-section h3 {
                color: #28a745;
                margin: 0 0 15px 0;
                font-size: 20px;
              }
              .emailPreview .certificate-image {
                max-width: 100%;
                height: auto;
                border: 1px solid #ddd;
                border-radius: 5px;
                margin: 15px 0;
              }
              .emailPreview .next-steps {
                background: #f8f9fa;
                padding: 25px;
                border-radius: 10px;
                margin: 25px 0;
                border-left: 4px solid #28a745;
              }
              .emailPreview .next-steps h3 {
                color: #28a745;
                margin: 0 0 15px 0;
                font-size: 20px;
              }
              .emailPreview .next-steps ul {
                margin: 0;
                padding-left: 20px;
              }
              .emailPreview .next-steps li {
                margin-bottom: 10px;
                color: #214F7D;
              }
              .emailPreview .footer {
                background: #214F7D;
                color: white;
                text-align: center;
                padding: 30px 20px;
                font-size: 14px;
              }
              .emailPreview .footer a {
                color: white;
                text-decoration: none;
              }
            `}
          </style>
          {emailPreviewLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2 }}>
              <CircularProgress size={50} />
              <Typography variant="body2" color="text.secondary">
                Loading email preview...
              </Typography>
            </Box>
          ) : emailPreviewData ? (
            <Box>
              {/* Email Details */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Peacekeeper ID:</strong> {emailPreviewData.peacekeeperId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Email:</strong> {emailPreviewData.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Subject:</strong> {emailPreviewData.subject}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Registration Link:</strong>{' '}
                      <a href={emailPreviewData.registrationLink} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                        {emailPreviewData.registrationLink}
                      </a>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Email HTML Preview */}
              <Box className="emailPreview" sx={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 2, 
                backgroundColor: '#fff'
              }}>
                <iframe
                  title="Email Preview"
                  srcDoc={emailPreviewData.emailMessage}
                  style={{
                    width: '100%',
                    minHeight: '500px',
                    border: 'none'
                  }}
                />
              </Box>

              {/* QR Code */}
              {emailPreviewData.qrCodeDataUrl && (
                <Box sx={{ mt: 3, textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                    QR Code
                  </Typography>
                  <img 
                    src={emailPreviewData.qrCodeDataUrl} 
                    alt="QR Code" 
                    style={{ 
                      maxWidth: '200px', 
                      height: 'auto',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px'
                    }} 
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
              No email preview available
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={handleResendEmail}
            disabled={resendingEmail || !emailPreviewData}
            variant="contained"
            startIcon={<EmailIcon />}
            sx={{
              minWidth: 150,
              backgroundColor: '#28a745',
              '&:hover': {
                backgroundColor: '#218838'
              }
            }}
          >
            {resendingEmail ? 'Sending...' : 'Resend Email'}
          </Button>
          <Button
            onClick={handleCloseEmailPreview}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
  open={Boolean(dateAnchorEl)}
  anchorEl={dateAnchorEl}
  onClose={handleDateClose}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  PaperProps={{ sx: { p: 2, width: 300, mt: 1 } }}
>
  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Select Range</Typography>
  <Box display="flex" flexDirection="column" gap={1}>
    <TextField
      label="From"
      type="date"
      fullWidth
      size="small"
      InputLabelProps={{ shrink: true }}
      value={dateRange.from}
      onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
    />
    <TextField
      label="To"
      type="date"
      fullWidth
      size="small"
      InputLabelProps={{ shrink: true }}
      inputProps={{ min: dateRange.from }}
      value={dateRange.to}
      onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
    />
    <Button variant="contained" onClick={handleDateClose} sx={{ bgcolor: "#214F7D" }} style={{marginLeft: "2px"}}>
      Apply
    </Button>
  </Box>
</Popover>
    </Box>
  );
};
export default UserManagement;